import { useToggle, useMediaQuery } from 'usehooks-ts';
import { createContext, PropsWithChildren, useMemo, useState } from 'react';

import DrawerMobileContainer from '../layouts/mobile-drawer/container';

type AvailableTabs = 'Profile' | 'Settings' | 'Support' | 'Notifications';

export interface LayoutProviderState {
  isMobile: boolean;
  navIsCollapsed: boolean;
  profileWindow: {
    visible: boolean;
    activeTab: AvailableTabs;
  };
  toggleNavIsCollapsed: () => void;
  drawerMobileMenu: React.ReactNode;
  setProfileWindow: React.Dispatch<
    React.SetStateAction<{
      visible: boolean;
      activeTab: AvailableTabs;
    }>
  >;
  setDrawerMobileMenu: React.Dispatch<React.SetStateAction<React.ReactNode>>;
}

const LayoutContext = createContext<LayoutProviderState>({
  isMobile: false,
  navIsCollapsed: true,
  profileWindow: {
    visible: false,
    activeTab: 'Profile'
  },
  setProfileWindow: () => {},
  toggleNavIsCollapsed: () => {},
  drawerMobileMenu: null,
  setDrawerMobileMenu: () => {}
});

/**
 * Creates a layout provider component that manages the state of the layout.
 * @param {PropsWithChildren} props - The properties passed to the component.
 * @returns {ReactElement} The rendered layout provider component.
 */
const LayoutProvider = (props: PropsWithChildren) => {
  const isMobile = useMediaQuery('(max-width:767px)');
  const [navIsCollapsed, toggleNavIsCollapsed] = useToggle(isMobile);
  const [drawerMobileMenu, setDrawerMobileMenu] =
    useState<React.ReactNode>(null);
  const [profileWindow, setProfileWindow] = useState<{
    visible: boolean;
    activeTab: AvailableTabs;
  }>({ visible: false, activeTab: 'Profile' });

  const value = useMemo(
    () => ({
      isMobile,
      profileWindow,
      navIsCollapsed,
      drawerMobileMenu,
      setProfileWindow,
      setDrawerMobileMenu,
      toggleNavIsCollapsed
    }),
    [isMobile, navIsCollapsed, profileWindow]
  );

  return (
    <LayoutContext.Provider value={value}>
      {isMobile ? (
        <DrawerMobileContainer>{props.children}</DrawerMobileContainer>
      ) : (
        props.children
      )}
    </LayoutContext.Provider>
  );
};

export { LayoutContext, LayoutProvider };
